import { z } from 'zod';

/**
 * Phase 2-G4 Track C Form 2 — public enrollment wizard validation.
 *
 * Mirrors the tenancy-service `src/schemas/enrollment.ts` field rules (SOP
 * field list) so the wizard validates client-side before POSTing. The tenancy
 * endpoint re-validates with the same rules — this is the UX layer, not the
 * security boundary. Kept in sync by hand (cross-service import isn't available
 * from frontend-app); a drift test asserts the regexes match.
 */

export const ENROLLMENT_NETWORKS = ['prevent', 'inform', 'resolve', 'rdr', 'ethoca'] as const;
export type EnrollmentNetwork = (typeof ENROLLMENT_NETWORKS)[number];

// G4 (audit C-1) — form-vocabulary names (matches src/lib/products.ts; kept
// inline here because this schema module is also the wizard's drift-tested
// validation mirror and stays dependency-light).
export const NETWORK_LABELS: Record<EnrollmentNetwork, string> = {
  prevent: 'Prevent CE3.0 (Order Insight)',
  inform: 'Inform',
  resolve: 'Resolve — CDRN',
  rdr: 'Resolve — RDR (Rapid Dispute Resolution)',
  ethoca: 'MC Resolve — Ethoca (Mastercard)',
};

export const merchantBasicsSchema = z.object({
  sellerLegalName: z.string().trim().min(1, 'Required').max(255),
  merchantNameOnWebsite: z.string().trim().min(1, 'Required').max(255),
  websiteUrl: z.string().trim().url('Must be a valid URL').max(2048),
  contactPhone: z.string().trim().min(7, 'Too short').max(32),
  contactEmail: z.string().trim().email('Must be a valid email').max(255),

  // C10 — fuller SOP onboarding-sheet fields (all optional; mirrors
  // tenancy `merchantBasicsSchema`). Kept byte-for-byte with the server rules.
  sellerProductBrand: z.string().trim().min(1).max(100).optional(),
  sellerProductDescription: z.string().trim().min(1).max(255).optional(),
  street: z.string().trim().min(1).max(200).optional(),
  city: z.string().trim().min(1).max(50).optional(),
  region: z.string().trim().min(1).max(50).optional(),
  postalCode: z.string().trim().min(1).max(50).optional(),
  /** ISO 3166-1 alpha-2 (matches Mid/Merchant.countryCode Char(2)). */
  countryCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, 'countryCode must be a 2-letter ISO code')
    .optional(),
  supportPhone: z.string().trim().min(7).max(50).optional(),
  supportEmail: z.string().trim().email().max(100).optional(),
  refundPolicyUrl: z.string().trim().url().max(2048).optional(),
  termsUrl: z.string().trim().url().max(2048).optional(),
});
export type MerchantBasics = z.infer<typeof merchantBasicsSchema>;

export const midDetailsSchema = z.object({
  midNumber: z.string().trim().min(1, 'Required').max(50),
  mcc: z.string().trim().regex(/^\d{4}$/, 'MCC must be a 4-digit code'),
  acquirerBin: z
    .string()
    .trim()
    .regex(/^4\d{5}$/, 'Acquirer BIN must be 6 digits starting with 4'),
  caid: z
    .string()
    .trim()
    .min(1, 'Required')
    .max(15)
    .regex(/^[A-Za-z0-9]+$/, 'CAID must be alphanumeric, max 15 characters'),
  descriptor: z.string().trim().min(1, 'Required').max(255),

  // C10 — fuller SOP per-MID fields (all optional; mirrors tenancy
  // `midDetailsSchema`). Kept byte-for-byte with the server rules.
  /** Extra `starts_with` statement descriptors beyond the primary `descriptor`. */
  descriptorVariations: z.array(z.string().trim().min(1).max(255)).max(20).optional(),
  bankMid: z.string().trim().min(1).max(50).optional(),
  bankTid: z.string().trim().min(1).max(50).optional(),
  /** Amount filter bounds (dollars; Mid.alert{Minimum,Maximum}Amount Decimal(18,2)). */
  alertMinimumAmount: z.number().nonnegative().optional(),
  alertMaximumAmount: z.number().nonnegative().optional(),
  /** Ethoca-only minimum alert amount — captured in submission_jsonb (no Mid column). */
  ethocaAmountThreshold: z.number().nonnegative().optional(),
});
export type MidDetails = z.infer<typeof midDetailsSchema>;

/**
 * C10 — optional RDR rule the wizard may collect when "rdr" is selected. SOP
 * vocabulary (spaced dropdown tokens), defaulting to the "Refund All" rule.
 * Mirrors tenancy `rdrRuleInputSchema`.
 */
export const rdrRuleInputSchema = z.object({
  name: z.string().trim().min(1).max(128).default('Refund All'),
  attribute: z.string().trim().min(1).max(64).default('Transaction Amount'),
  operator: z.string().trim().min(1).max(64).default('Greater Than or Equals'),
  value: z.string().trim().min(1).max(255).default('0.01'),
});
export type RdrRuleInput = z.infer<typeof rdrRuleInputSchema>;

export const publicEnrollSubmissionSchema = z.object({
  merchant: merchantBasicsSchema,
  mid: midDetailsSchema,
  networks: z.array(z.enum(ENROLLMENT_NETWORKS)).min(1, 'Select at least one service'),
  rdrRule: rdrRuleInputSchema.optional(),
});
export type PublicEnrollSubmission = z.infer<typeof publicEnrollSubmissionSchema>;
